import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { CalendarPlus, Check, HardHat, Loader2, Mail, Minus, Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DateField } from "@/components/common/DateField";
import { api, errMsg } from "@/lib/api";
import { fmtDate, toDateStr } from "@/lib/format";

/* ----------------------------------------------------------------------------
 * Book trade in (Phase 91A) — one-tap booking for the trade responsible for a defect.
 * Defaults: next business day · 1 working day · email the trade a confirm / propose link.
 * Posts to /bookings with defect_id so the booking is linked + logged on the defect's history.
 * -------------------------------------------------------------------------- */

const isWorkday = (d) => d.getDay() !== 0 && d.getDay() !== 6;
/** First Mon–Fri strictly after today (local). */
export const nextBusinessDay = (from = new Date()) => {
  const d = new Date(from.getFullYear(), from.getMonth(), from.getDate() + 1);
  while (!isWorkday(d)) d.setDate(d.getDate() + 1);
  return toDateStr(d);
};
/** Inclusive end date covering `days` working days from `start` (weekends skipped). */
export const endAfterWorkdays = (start, days) => {
  if (!start) return "";
  const d = new Date(`${start.slice(0, 10)}T00:00:00`);
  if (Number.isNaN(d.getTime())) return "";
  let left = Math.max(1, parseInt(days, 10) || 1) - (isWorkday(d) ? 1 : 0);
  while (left > 0) {
    d.setDate(d.getDate() + 1);
    if (isWorkday(d)) left -= 1;
  }
  return toDateStr(d);
};

export const BOOKING_STATUS_META = {
  pending: { label: "Awaiting trade", cls: "border-amber-500/40 bg-amber-500/10 text-amber-300" },
  awaiting_approval: { label: "Trade proposed a change", cls: "border-orange-500/40 bg-orange-500/10 text-orange-300" },
  confirmed: { label: "Confirmed", cls: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300" },
  tentative: { label: "Tentative", cls: "border-border bg-muted/40 text-muted-foreground" },
  completed: { label: "Completed", cls: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300" },
  declined: { label: "Trade declined", cls: "border-rose-500/40 bg-rose-500/10 text-rose-300" },
  rejected: { label: "Change rejected", cls: "border-rose-500/40 bg-rose-500/10 text-rose-300" },
};

/**
 * props: defect (id, number, job_id, contractor_id, contractor_name, contractor_email, trade, area, location, description),
 *        contractors (optional preloaded list), onBooked(booking, result), onDismiss, testPrefix
 */
export const BookTradeCard = ({ defect, contractors: preloaded, onBooked, onDismiss, testPrefix = "book-trade" }) => {
  const [contractors, setContractors] = useState(preloaded || []);
  const [contractorId, setContractorId] = useState(defect?.contractor_id || "");
  const [start, setStart] = useState(() => nextBusinessDay());
  const [days, setDays] = useState(1);
  const [sendRequest, setSendRequest] = useState(true);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { setContractorId(defect?.contractor_id || ""); }, [defect?.id, defect?.contractor_id]);
  useEffect(() => {
    if (preloaded?.length) { setContractors(preloaded); return; }
    api.get("/contractors").then((r) => setContractors(r.data || [])).catch(() => {});
  }, [preloaded]);

  const contractor = useMemo(() => contractors.find((c) => c.id === contractorId) || null, [contractors, contractorId]);
  const email = (contractor?.email || (contractorId === defect?.contractor_id ? defect?.contractor_email : "") || "").trim();
  const name = contractor?.name || (contractorId === defect?.contractor_id ? defect?.contractor_name : "") || "";
  const end = useMemo(() => endAfterWorkdays(start, days), [start, days]);
  const where = [defect?.area, defect?.location].filter(Boolean).join(" — ");

  const book = async () => {
    if (!contractorId) { toast.error("Pick the trade to book"); return; }
    if (!start) { toast.error("Pick a start date"); return; }
    setBusy(true);
    try {
      const title = `${defect?.number || "Defect"} — ${(defect?.description || "Rectification").slice(0, 60)}${(defect?.description || "").length > 60 ? "…" : ""}`;
      const res = await api.post("/bookings", {
        job_id: defect.job_id,
        contractor_id: contractorId,
        title,
        start_date: start,
        end_date: end,
        status: "confirmed",
        notes: note.trim(),
        send_request: sendRequest && Boolean(email),
        add_to_schedule: true,
        defect_id: defect.id,
      });
      const { booking, conflicts = [], request_sent, request_error } = res.data;
      if (conflicts.length) {
        const c = conflicts[0];
        toast.warning(`Heads up: ${name || "the trade"} is also booked on "${c.job_name}" ${fmtDate(c.start_date)} → ${fmtDate(c.end_date)}`, { duration: 8000 });
      }
      if (request_sent) toast.success(`${name} booked for ${fmtDate(start)} — confirm / propose link emailed to ${email}`, { duration: 7000 });
      else if (request_error) toast.warning(request_error, { duration: 8000 });
      else toast.success(`${name || "Trade"} booked for ${fmtDate(start)}`);
      onBooked?.(booking, res.data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-xl border border-primary/30 bg-accent/40 p-3" data-testid={`${testPrefix}-card`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-primary"><CalendarPlus className="h-3 w-3" /> Book trade in</p>
          <p className="truncate text-sm font-medium" data-testid={`${testPrefix}-defect`}>{defect?.number}{where ? <span className="font-normal text-muted-foreground"> · {where}</span> : null}</p>
        </div>
        {onDismiss && (
          <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={onDismiss} aria-label="Not now" data-testid={`${testPrefix}-dismiss`}><X className="h-4 w-4" /></Button>
        )}
      </div>

      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <div className="space-y-1.5 sm:col-span-2">
          <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">Trade</Label>
          {defect?.contractor_id && contractorId === defect.contractor_id ? (
            <div className="flex min-h-10 items-center gap-2 rounded-lg border border-border bg-background px-3 py-1.5 text-sm" data-testid={`${testPrefix}-contractor-fixed`}>
              <HardHat className="h-4 w-4 shrink-0 text-muted-foreground" />
              <span className="min-w-0 flex-1 truncate">{name}{defect.trade ? <span className="text-muted-foreground"> · {defect.trade}</span> : null}</span>
              <button type="button" className="text-xs font-medium text-primary hover:underline" onClick={() => setContractorId("")} data-testid={`${testPrefix}-change-contractor`}>Change</button>
            </div>
          ) : (
            <Select value={contractorId} onValueChange={setContractorId}>
              <SelectTrigger className="h-10" data-testid={`${testPrefix}-contractor`}><SelectValue placeholder="Pick the trade to book" /></SelectTrigger>
              <SelectContent className="max-h-64">
                {contractors.length === 0 ? <div className="px-3 py-2 text-sm text-muted-foreground">No contractors in the directory yet</div> : null}
                {contractors.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}{c.trade ? ` · ${c.trade}` : ""}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>
        <div className="space-y-1.5">
          <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">Start</Label>
          <DateField value={start} onChange={setStart} testId={`${testPrefix}-start`} />
        </div>
        <div className="space-y-1.5">
          <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">Working days</Label>
          <div className="flex items-center gap-1.5">
            <Button type="button" variant="outline" size="icon" className="h-10 w-10 shrink-0" onClick={() => setDays((d) => Math.max(1, d - 1))} disabled={days <= 1} aria-label="One day less" data-testid={`${testPrefix}-days-minus`}><Minus className="h-4 w-4" /></Button>
            <Input type="number" min={1} max={30} value={days} onChange={(e) => setDays(Math.min(30, Math.max(1, parseInt(e.target.value, 10) || 1)))} className="h-10 w-16 text-center tabular-nums" aria-label="Working days" data-testid={`${testPrefix}-days`} />
            <Button type="button" variant="outline" size="icon" className="h-10 w-10 shrink-0" onClick={() => setDays((d) => Math.min(30, d + 1))} disabled={days >= 30} aria-label="One day more" data-testid={`${testPrefix}-days-plus`}><Plus className="h-4 w-4" /></Button>
            <span className="ml-1 truncate text-xs text-muted-foreground" data-testid={`${testPrefix}-end`}>{end && end !== start ? `to ${fmtDate(end)}` : ""}</span>
          </div>
        </div>
        <div className="space-y-1.5 sm:col-span-2">
          <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">Note for the trade (optional)</Label>
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Access, materials, who to call on site…" maxLength={400} className="h-10" data-testid={`${testPrefix}-note`} />
        </div>
      </div>

      <div className="mt-3 flex items-start justify-between gap-3 rounded-lg border border-border bg-background p-2.5" data-testid={`${testPrefix}-email-row`}>
        <div className="min-w-0">
          <p className="flex items-center gap-1.5 text-sm font-medium"><Mail className="h-3.5 w-3.5 text-muted-foreground" /> Email a confirm / propose link</p>
          <p className="mt-0.5 text-xs text-muted-foreground" data-testid={`${testPrefix}-email-hint`}>
            {!contractorId ? "Pick a trade first." : email ? `${name} confirms or proposes another date via a secure link (${email}). The defect's photos and details are included.` : `${name || "This trade"} has no email on file — the booking is saved without a request.`}
          </p>
        </div>
        <Switch checked={sendRequest && Boolean(email)} onCheckedChange={setSendRequest} disabled={!email} aria-label="Email a booking request" data-testid={`${testPrefix}-send-request`} />
      </div>

      <div className="mt-3 flex items-center justify-end gap-2">
        {onDismiss && <Button type="button" variant="ghost" size="sm" className="h-10" onClick={onDismiss} data-testid={`${testPrefix}-not-now`}>Not now</Button>}
        <Button type="button" size="sm" className="h-10" onClick={book} disabled={busy || !contractorId || !start} data-testid={`${testPrefix}-submit`}>
          {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Check className="mr-1.5 h-4 w-4" />}
          {busy ? "Booking…" : sendRequest && email ? "Book & send request" : "Book trade in"}
        </Button>
      </div>
    </div>
  );
};
